import React from "react";
import { motion } from "framer-motion";

/* ---------- SEO Schema ---------- */
function SeoSchema() {
  const restaurantSchema = {
    "@context": "https://schema.org",
    "@type": ["Restaurant", "LocalBusiness"],
    name: "The Pahadi Bistro",
    description:
      "Nepali and Indo-Chinese restaurant in Kharadi, Pune serving pahadi-style comfort food, handcrafted momos, noodles, and Indo-Chinese classics.",
    servesCuisine: ["Nepali", "Indo-Chinese", "Pahadi", "Momo", "Noodles"],
    address: {
      "@type": "PostalAddress",
      streetAddress:
        "The Pahadi Bistro, Shop No.2, Global Highstreet, Global Precioso Rd, near Eon Free Zone Road",
      addressLocality: "Kharadi",
      addressRegion: "Maharashtra",
      postalCode: "411014",
      addressCountry: "IN",
    },
    areaServed: [
      { "@type": "City", name: "Pune" },
      { "@type": "Place", name: "Kharadi" },
    ],
    url: "https://thepahadibistro.com/",
    sameAs: ["https://maps.app.goo.gl/pFED83LLDGAwdcBk6"],
    telephone: "+91-9373309484",
    priceRange: "$$",
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Where can I find the best momos near me in Kharadi?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "The Pahadi Bistro in Global High Street, Kharadi serves handcrafted momos including jhol momos and pan-fried momos, inspired by pahadi-style flavours.",
        },
      },
      {
        "@type": "Question",
        name: "Do you serve Indo-Chinese noodles in Kharadi, Pune?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. We serve flavour-packed Indo-Chinese noodles such as Hakka noodles along with classic favourites like chilli chicken and house-made sauces.",
        },
      },
      {
        "@type": "Question",
        name: "What type of cuisine is The Pahadi Bistro known for?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We are known for Nepali and Indo-Chinese cuisine with pahadi-style comfort food, momos, noodles, and rich gravies.",
        },
      },
      {
        "@type": "Question",
        name: "Where is The Pahadi Bistro located?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "We are located at Global High Street, Kharadi, Pune, Maharashtra 411014, India.",
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(restaurantSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}

/* ---------- Fade-in wrapper ---------- */
function FadeInUp({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ---------- Component ---------- */
export default function About() {
  return (
    <section id="about" className="section-padding bg-cream overflow-hidden">
      <SeoSchema />

      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <FadeInUp>
            <h2 className="section-heading">Our Story</h2>
            <div className="accent-divider" />
          </FadeInUp>
        </div>

        {/* Two-column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Image Side */}
          <FadeInUp delay={0.1}>
            <div className="relative">
              <img
                src="/assets/main.jpg"
                alt="The Pahadi Bistro restaurant — pahadi-style cuisine in Kharadi, Pune"
                className="w-full h-[350px] md:h-[450px] object-cover rounded-2xl shadow-xl"
                loading="lazy"
              />
              {/* Decorative accent */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-gold/20 rounded-2xl -z-10 hidden md:block" />
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-redAccent/10 rounded-2xl -z-10 hidden md:block" />
            </div>
          </FadeInUp>

          {/* Text Side */}
          <div className="space-y-6">
            <FadeInUp delay={0.2}>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                Welcome to{" "}
                <strong className="text-black">The Pahadi Bistro</strong> — a
                Nepali and Indo-Chinese restaurant in Kharadi, Pune, where
                authentic pahadi flavours meet a modern dining experience.
              </p>
            </FadeInUp>

            <FadeInUp delay={0.3}>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                Located at{" "}
                <a
                  href="https://maps.app.goo.gl/pFED83LLDGAwdcBk6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-redAccent underline underline-offset-2 font-medium hover:text-redDark transition-colors"
                >
                  Global High Street, Kharadi
                </a>
                , we craft every dish with care — from our signature{" "}
                <strong>jhol momos</strong> and pan-fried momos to flavour-packed{" "}
                <strong>Hakka noodles</strong>, chilli chicken, and rich gravies
                made with traditional techniques and house-made sauces.
              </p>
            </FadeInUp>

            <FadeInUp delay={0.4}>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                Set in one of Pune's most vibrant cafe hubs, we offer a relaxed
                yet lively dining experience — perfect for casual dinners, group
                outings, and late-evening cravings. Come savour the bold
                pahadi-style flavours that keep guests coming back.
              </p>
            </FadeInUp>

            {/* Stats / highlights */}
            <FadeInUp delay={0.5}>
              <div className="grid grid-cols-3 gap-4 pt-4">
                {[
                  { number: "50+", label: "Dishes" },
                  { number: "4.6★", label: "Rating" },
                  { number: "100%", label: "Fresh" },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="text-center p-4 bg-beige rounded-xl"
                  >
                    <div className="text-2xl md:text-3xl font-display font-bold text-redAccent">
                      {stat.number}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </FadeInUp>
          </div>
        </div>
      </div>
    </section>
  );
}
